import { motion } from 'framer-motion';
import { ExternalLink, Code2, Zap } from 'lucide-react';
import Navbar from '../components/Navbar';

const projects = [
  {
    title: 'Professional Yapper',
    org: 'Life · Full-time',
    desc: 'Talking to people, making conversations happen, and somehow always having something to say. A natural born skill.',
    tags: ['Talking', 'Networking', 'Persuasion', 'Vibes'],
    status: 'Present',
    duration: 'Since Birth – Present',
    num: '00',
  },
  {
    title: 'Teaching Assistant — ENGG',
    org: 'LUMS · Part-time',
    paid: true,
    desc: 'Assisting the course instructor with an Engineering course — running tutorials, helping students during office hours, and grading assignments.',
    tags: ['Teaching Assistant', 'Engineering', 'LUMS', 'Mentoring'],
    status: 'Present',
    duration: 'Present',
    num: '01',
  },
  {
    title: 'Director Registrations & Accommodations',
    org: 'SPADES',
    desc: 'Managing registrations and coordination for events under SPADES society.',
    tags: ['Event Management', 'Coordination', 'Operations'],
    status: 'Present',
    duration: 'Jun 2026 – Present',
    num: '02',
  },
  {
    title: 'A Levels Tutor — CS, Physics & Maths',
    org: 'Private · Part-time',
    paid: true,
    desc: 'Privately tutoring a couple of students in A Levels Computer Science, Physics, and Mathematics.',
    tags: ['Teaching', 'A Levels', 'CS', 'Physics', 'Maths', 'Private Tutor'],
    status: 'Present',
    duration: 'Mar 2026 – Present',
    num: '03',
  },
  {
    title: 'SAT Coach',
    org: 'Personal / Lumora Prep · Part-time',
    paid: true,
    desc: 'Coaching students on SAT prep strategies, focusing on reading and writing sections to maximize scores.',
    tags: ['Teaching', 'SAT', 'Mentoring', 'Part-time'],
    status: 'Present',
    duration: 'Jan 2026 – Present',
    num: '04',
  },
  {
    title: 'Profile Builder',
    org: 'Invicta Career Counseling · Part-time · Remote',
    paid: true,
    desc: 'Helped students build strong university application profiles through personalized career counseling and guidance.',
    tags: ['Career Counseling', 'Remote', 'Student Profiles', 'Consulting'],
    status: 'Completed',
    duration: 'Sep 2025 – Jan 2026 · 5 mos',
    num: '05',
  },
  {
    title: 'Member — Outgoing Talent',
    org: 'AIESEC · Part-time · Asia · Hybrid',
    desc: 'Facilitated outgoing exchange opportunities for students across Asia, connecting talent with global internship programs.',
    tags: ['Leadership', 'Global Exchange', 'Networking', 'Hybrid'],
    status: 'Completed',
    duration: 'Aug 2025 – Jan 2026 · 6 mos',
    num: '06',
  },
];

const statusColors = {
  'Completed': 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30',
  'In Progress': 'text-amber-400 bg-amber-400/10 border-amber-400/30',
  'Live': 'text-primary bg-primary/10 border-primary/30',
  'Present': 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30',
};

export default function Projects() {
  return (
    <div className="min-h-screen bg-background dot-bg">
      <Navbar />
      <div className="max-w-4xl mx-auto px-8 pt-32 pb-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="text-xs font-semibold text-primary bg-primary/10 border border-primary/30 px-3 py-1 rounded-full font-mono-cv tracking-widest uppercase">Experience</span>
          <h2 className="text-4xl font-extrabold text-foreground mt-4 mb-3" style={{ fontFamily: 'Syne, sans-serif' }}>Things I Do</h2>
          <p className="text-muted-foreground text-sm mb-12">things i do… some planned, some not</p>
        </motion.div>

        <div className="space-y-4">
          {projects.map((project, i) => (
            <motion.div
              key={project.title}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-card border border-border rounded-xl p-6 hover:border-primary/40 transition-all duration-300 group flex items-start gap-6"
            >
              <span className="font-mono-cv text-3xl font-bold text-border group-hover:text-primary/20 transition-colors flex-shrink-0 leading-none mt-1">
                {project.num}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4 mb-1 flex-wrap">
                  <h3 className="font-extrabold text-foreground text-lg group-hover:text-primary transition-colors" style={{ fontFamily: 'Syne, sans-serif' }}>
                    {project.title}
                  </h3>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {project.paid && (
                      <span className="font-mono-cv text-xs px-2 py-0.5 rounded-full border text-yellow-400 bg-yellow-400/10 border-yellow-400/30 flex items-center gap-1">
                        💵 Paid
                      </span>
                    )}
                    <span className={`font-mono-cv text-xs px-2 py-0.5 rounded-full border ${statusColors[project.status]}`}>
                      {project.status}
                    </span>
                  </div>
                </div>
                <p className="text-primary/70 text-xs font-mono-cv mb-1">{project.org}</p>
                <p className="text-muted-foreground/60 text-xs font-mono-cv mb-2">{project.duration}</p>
                <p className="text-muted-foreground text-sm leading-relaxed mb-3">{project.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {project.tags.map(tag => (
                    <span key={tag} className="font-mono-cv text-xs text-muted-foreground bg-secondary px-2 py-0.5 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}